import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { AthenaAdjunto } from '@conversia/shared-types';
import { ArrowUp, Mic, Paperclip } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { enterEnvia, nombreDeGrabacion, porQueNoGraba, tiempoDeGrabacion } from '@/lib/athena-hilos';
import { FichaEnCola, type EstadoDeSubida } from './ConversacionDeAthena';

/**
 * EL CAMPO DE ESCRIBIRLE A ATHENA (tanda 6): un área que crece, el clip, el micrófono y enviar.
 * Kervens: «que yo le pueda subir documentos, PDF, fotos, mandarle un audio».
 *
 * El texto y los archivos en cola son de la página (una sugerencia escribe en el campo; soltar
 * un archivo sobre la conversación lo pone en la cola). Lo que sí es de aquí es la GRABACIÓN:
 * pide el micrófono, cuenta el tiempo y, con «Listo», entrega el archivo para que se suba como
 * cualquier otro. Si el navegador no graba o no hay permiso, lo dice y no pasa nada más.
 */

/** Un archivo sobre el campo: subiendo, listo para irse con el mensaje, o rechazado. */
export interface ArchivoEnCola {
  clave: string;
  nombre: string;
  tamano: number;
  estado: EstadoDeSubida;
  /** Lo que dijo el servidor, si lo rechazó. */
  error?: string;
  /** Lo que devolvió el servidor al subirlo. */
  adjunto?: AthenaAdjunto;
}

/** Lo que la página puede pedirle al campo. */
export interface ManejoDelCampo {
  enfocar: () => void;
  elegirArchivos: () => void;
}

export interface CampoDeAthenaProps {
  texto: string;
  onTexto: (texto: string) => void;
  cola: readonly ArchivoEnCola[];
  onQuitar: (clave: string) => void;
  /** Lo elegido con el clip. */
  onArchivos: (archivos: File[]) => void;
  /** Una grabación terminada con «Listo». */
  onGrabacion: (archivo: File) => void;
  onEnviar: () => void;
  puedeEnviar: boolean;
}

/** Seis renglones de 24 px más el relleno: de ahí en adelante el campo se desplaza por dentro. */
const ALTO_MAXIMO = 164;

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600';
const REDONDO = cn(
  'grid h-11 w-11 shrink-0 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  FOCO,
);

interface Grabadora {
  rec: MediaRecorder;
  stream: MediaStream;
  trozos: Blob[];
  cancelada: boolean;
}

export const CampoDeAthena = forwardRef<ManejoDelCampo, CampoDeAthenaProps>(function CampoDeAthena(
  { texto, onTexto, cola, onQuitar, onArchivos, onGrabacion, onEnviar, puedeEnviar },
  ref,
) {
  const toast = useToast();
  const area = useRef<HTMLTextAreaElement>(null);
  const archivos = useRef<HTMLInputElement>(null);
  const grabadora = useRef<Grabadora | null>(null);
  const [grabandoDesde, setGrabandoDesde] = useState<number | null>(null);
  const [transcurrido, setTranscurrido] = useState(0);
  // La grabación termina mucho después de empezar: que entregue al `onGrabacion` de AHORA.
  const alGrabar = useRef(onGrabacion);
  alGrabar.current = onGrabacion;

  useImperativeHandle(ref, () => ({ enfocar: () => area.current?.focus(), elegirArchivos: () => archivos.current?.click() }), []);

  // El área crece con lo escrito, hasta seis renglones.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, ALTO_MAXIMO)}px`;
  }, [texto, grabandoDesde]);

  useEffect(() => {
    if (grabandoDesde === null) return;
    const t = setInterval(() => setTranscurrido(Date.now() - grabandoDesde), 250);
    return () => clearInterval(t);
  }, [grabandoDesde]);

  // Salir de la pantalla a media grabación suelta el micrófono (y no sube nada).
  useEffect(
    () => () => {
      const g = grabadora.current;
      if (!g) return;
      g.cancelada = true;
      if (g.rec.state !== 'inactive') g.rec.stop();
      g.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  const grabar = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.warning('No se puede grabar aquí', porQueNoGraba('sin-soporte'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const g: Grabadora = { rec, stream, trozos: [], cancelada: false };
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) g.trozos.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        grabadora.current = null;
        setGrabandoDesde(null);
        if (g.cancelada) return;
        const mime = rec.mimeType || g.trozos[0]?.type || 'audio/webm';
        const audio = new Blob(g.trozos, { type: mime });
        if (audio.size === 0) {
          toast.warning('No se grabó nada', 'Vuelve a tocar el micrófono y habla un momento antes de «Listo».');
          return;
        }
        alGrabar.current(new File([audio], nombreDeGrabacion(mime, new Date()), { type: mime }));
      };
      grabadora.current = g;
      rec.start();
      setTranscurrido(0);
      setGrabandoDesde(Date.now());
    } catch (e) {
      toast.warning('No se pudo grabar', porQueNoGraba(e instanceof Error ? e.name : ''));
    }
  };

  const terminar = (cancelada: boolean) => {
    const g = grabadora.current;
    if (!g) return;
    g.cancelada = cancelada;
    if (g.rec.state !== 'inactive') g.rec.stop();
  };

  return (
    <div className="shrink-0 px-3 pb-2 pt-1 md:px-6 md:pb-3">
      <div className="mx-auto w-full max-w-[720px]">
        {cola.length > 0 && (
          <ul aria-label="Archivos para mandar" className="mb-2 flex flex-wrap gap-2">
            {cola.map((a) => (
              <FichaEnCola key={a.clave} nombre={a.nombre} tamano={a.tamano} estado={a.estado} error={a.error} onQuitar={() => onQuitar(a.clave)} />
            ))}
          </ul>
        )}

        <div
          className={cn(
            'flex items-end gap-1 rounded-[1.25rem] border border-surface-border bg-surface p-1.5 shadow-lg shadow-slate-900/5 transition-colors dark:border-slate-700 dark:shadow-black/30',
            'focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/20 dark:focus-within:border-brand-500',
          )}
        >
          {grabandoDesde !== null ? (
            <div role="group" aria-label="Grabando un audio" className="flex min-h-[44px] flex-1 items-center gap-3 pl-2.5">
              {/* El punto que late: se está grabando. Quieto si la persona pidió menos movimiento. */}
              <span aria-hidden className="relative grid h-3 w-3 shrink-0 place-items-center">
                <span className="absolute h-3 w-3 rounded-full bg-red-500 motion-safe:animate-latido" />
                <span className="h-3 w-3 rounded-full bg-red-500" />
              </span>
              <span role="timer" className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-50">
                {tiempoDeGrabacion(transcurrido)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-500 dark:text-slate-400">Grabando…</span>
              <button
                type="button"
                onClick={() => terminar(true)}
                className={cn('h-11 rounded-xl px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800', FOCO)}
              >
                Cancelar
              </button>
              <button type="button" onClick={() => terminar(false)} className={cn('h-11 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700', FOCO)}>
                Listo
              </button>
            </div>
          ) : (
            <>
              <button type="button" onClick={() => archivos.current?.click()} aria-label="Adjuntar archivos" title="Adjuntar archivos (hasta 25 MB cada uno)" className={REDONDO}>
                <Paperclip className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => void grabar()} aria-label="Grabar un audio" title="Grabar un audio" className={REDONDO}>
                <Mic className="h-5 w-5" />
              </button>
              <textarea
                ref={area}
                rows={1}
                value={texto}
                maxLength={4000}
                onChange={(e) => onTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (!enterEnvia({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing }, window.matchMedia('(pointer: coarse)').matches)) return;
                  e.preventDefault();
                  if (puedeEnviar) onEnviar();
                }}
                aria-label="Escríbele a Athena"
                placeholder="Escríbele a Athena…"
                // 16 px en el teléfono: con menos, el iPhone acerca la pantalla al enfocar.
                className="min-h-[44px] min-w-0 flex-1 resize-none self-center bg-transparent px-1.5 py-2.5 text-base leading-6 text-slate-900 caret-brand-600 outline-none placeholder:text-slate-500 md:text-[15px] dark:text-slate-100 dark:placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={onEnviar}
                disabled={!puedeEnviar}
                aria-label="Enviar"
                className={cn(
                  'grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-600 text-white transition enabled:hover:bg-brand-700 enabled:active:scale-95 motion-reduce:enabled:active:scale-100',
                  'disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-600',
                  FOCO,
                )}
              >
                <ArrowUp className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {/* Sin `accept`: qué es cada archivo lo decide el servidor por su firma, y dice por qué no recibe uno. */}
        <input
          ref={archivos}
          type="file"
          multiple
          hidden
          tabIndex={-1}
          aria-hidden
          onChange={(e) => {
            const elegidos = Array.from(e.target.files ?? []);
            // Vacío de nuevo: elegir dos veces el mismo archivo tiene que volver a avisar.
            e.target.value = '';
            if (elegidos.length) onArchivos(elegidos);
          }}
        />
      </div>
    </div>
  );
});
